import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Hero } from './components/Hero';
import { TrustBadges } from './components/TrustBadges';
import { initPixel, trackPageView, PIXEL_NOSCRIPT_URL } from './lib/pixel';
// Code-split below-the-fold sections so they don't block initial paint
const Hook = lazy(() => import('./components/Hook').then(m => ({
  default: m.Hook
})));
const Benefits = lazy(() => import('./components/Benefits').then(m => ({
  default: m.Benefits
})));
const Modules = lazy(() => import('./components/Modules').then(m => ({
  default: m.Modules
})));
const Gallery = lazy(() => import('./components/Gallery').then(m => ({
  default: m.Gallery
})));
const Creator = lazy(() => import('./components/Creator').then(m => ({
  default: m.Creator
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Offer = lazy(() => import('./components/Offer').then(m => ({
  default: m.Offer
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const StickyCTA = lazy(() => import('./components/StickyCTA').then(m => ({
  default: m.StickyCTA
})));
const SectionFallback = () => <div style={{
  minHeight: '400px'
}} data-id="element-0" />;
// Run resource hints SYNCHRONOUSLY at module load — before React mounts.
// This lets DNS lookups start happening while the rest of the bundle parses.
if (typeof document !== 'undefined') {
  const hints: {
    rel: string;
    href: string;
    as?: string;
    crossOrigin?: string;
  }[] = [{
    rel: 'preconnect',
    href: 'https://cdn.magicpatterns.com',
    crossOrigin: ''
  }, {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
    crossOrigin: ''
  }, {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: ''
  }, {
    rel: 'preconnect',
    href: 'https://images.unsplash.com',
    crossOrigin: ''
  }, {
    rel: 'preconnect',
    href: 'https://randomuser.me',
    crossOrigin: ''
  }, {
    rel: 'preconnect',
    href: 'https://connect.facebook.net',
    crossOrigin: ''
  },
  // Preload the hero image so it starts downloading in parallel
  {
    rel: 'preload',
    as: 'image',
    href: 'https://cdn.magicpatterns.com/uploads/iGBbbQTSfakhwkXMZk7FHR/Mockup_Master_Reposteria.webp'
  }];
  hints.forEach(hint => {
    const selector = `link[rel="${hint.rel}"][href="${hint.href}"]`;
    if (document.querySelector(selector)) return;
    const link = document.createElement('link');
    link.rel = hint.rel;
    link.href = hint.href;
    if (hint.as) link.setAttribute('as', hint.as);
    if (hint.crossOrigin !== undefined) link.crossOrigin = hint.crossOrigin;
    document.head.appendChild(link);
  });
}
export function App() {
  useEffect(() => {
    // Defer pixel until browser is idle so it never blocks LCP/FCP
    const idle = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1500));
    idle(() => {
      initPixel();
      trackPageView();
    });
  }, []);
  return <div className="min-h-screen bg-cream font-sans selection:bg-caramel/30 selection:text-espresso" data-id="element-1">
      <noscript data-id="element-2">
        <img height="1" width="1" style={{
        display: 'none'
      }} src={PIXEL_NOSCRIPT_URL} alt="" data-id="element-3" />
      </noscript>

      {/* Above-the-fold: load eagerly */}
      <Hero data-id="element-4" />
      <TrustBadges data-id="element-5" />

      {/* Below-the-fold: code-split + suspense */}
      <Suspense fallback={<SectionFallback data-id="element-7" />} data-id="element-6">
        <Hook data-id="element-8" />
        <Benefits data-id="element-9" />
        <Modules data-id="element-10" />
        <Gallery data-id="element-11" />
        <Creator data-id="element-12" />
        <Testimonials data-id="element-13" />
        <Guarantee data-id="element-14" />
        <Offer data-id="element-15" />
        <FAQ data-id="element-16" />
        <Footer data-id="element-17" />
        <StickyCTA data-id="element-18" />
      </Suspense>
    </div>;
}